import React, { useEffect } from 'react';
import { Hidden } from '@material-ui/core';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useRouteMatch } from 'react-router-dom';
import { EmploymentState } from '../employment/employment-model';
import { TcsmStates } from '../../../../root/tcsm-model';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';
import { getEmployments } from '../../../../global/ducks/benadmin-api';
import { GetEmploymentsAndCompensations } from './employment-compensation-requests';
import { EmploymentCompensationState } from './employment-compensation-models';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { CompensationProps, EmploymentProps } from '../../models/arc-epi-props';
import { EmploymentCompensationItem } from './employment-compensation-item';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { genericAccordionStyles } from '../../../../global/constants/theme/styles/components/generic-mui-accordion';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { AdminOfficer, SeniorOfficer } from '../../constants/constant-strings';
import { useRoleObject } from '../../components/useRoles';
import { stringDateSort } from '../../../../global/helpers/sort-helper';
import { clearEmploymentCompensation } from './employment-compensation-slices';
import { apiErrorClear } from '../../../../global/ducks/base-api';


export function EmploymentCompensation() {

    const employmentState = useSelector<TcsmStates, EmploymentState>(state => state?.employmentState);
    const employmentCompState = useSelector<TcsmStates, EmploymentCompensationState>(state => state?.employmentCompensationState);
    const routeMatch = useRouteMatch();
    const dispatch = useDispatch();
    const gpsItem = useGpsType(GpsType.EMPLOYMENT_COMPENSATION);
    const epiProps = useSelector<TcsmStates, EmploymentProps>(state => state?.epiProps?.employmentSettingsState?.epiContent);
    const compProps = useSelector<TcsmStates, CompensationProps>(state => state?.epiProps?.compensationSettingsState?.epiContent);
    const labelProps = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);    
    const roles = useRoleObject();

    const sortedEmployment = employmentCompState?.employmentCompensation?.sort((a, b) =>{
        return stringDateSort(a?.employment?.hire_date, b?.employment?.hire_date);
    });

    
    const accordionClasses = genericAccordionStyles();
    useEffect(() => {
        if (employmentState?.currentEmployee) {
            dispatch(getEmployments(
                employmentState?.currentEmployee?.guid,
                (response) => GetEmploymentsAndCompensations(dispatch, response),
                () => {
                    dispatch(clearEmploymentCompensation());
                    dispatch(apiErrorClear());
                },
            ));
        }
    }, [employmentState?.currentEmployee?.guid]);

    useEffect(() => {
        return function cleanUp() {
            dispatch(clearEmploymentCompensation());
        };
    }, []);

    if (!employmentState?.currentEmployee) {

        const participantSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.Participants}`);
        return (
            <Redirect to=
                {
                    {
                        pathname: participantSelect,
                        state: {
                            actionLink: routeMatch.path,
                        },
                    }
                }
            />);

    }

    if (!employmentCompState?.employmentCompensationLoaded) {
        return <LoadingCircle />;
    }

    function IsInstitutionAdmin() {        
        const erRoles = roles?.employee_roster?.map(a => a.role_name);
        return erRoles?.includes(SeniorOfficer) || erRoles?.includes(AdminOfficer);
    }

    function RenderNotAuthorized(){
        return (<WysiwygWithReactLink content={compProps?.unauthorizedMessage || 'Unauthorized'} /> );
    }

    const firstNonTermiantedEmployment = employmentCompState?.employmentCompensation?.find(employmentCompensation => !(employmentCompensation?.employment?.termination_date)) || sortedEmployment[0];


    return (
        <>
            <PageContainer>
                <MainContent>
                    <PageHeader
                        headerText={epiProps?.employmentCompensationTitle}
                        description={epiProps?.employmentCompensationDescription} />
                    <Hidden mdUp>
                        <SideBarContent>
                            <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                        </SideBarContent>
                    </Hidden>
                    { IsInstitutionAdmin() ?
                    <div className={accordionClasses.accordionContainer}>
                        {
                            employmentCompState?.employmentCompensation.map(ec => {
                                return (
                                    <EmploymentCompensationItem
                                        key={ec?.employment?.id}
                                        settings={epiProps}
                                        compensationSettings={compProps}
                                        data={ec}
                                        labelSettings={labelProps}   
                                        isLatestEmployent = {ec?.employment?.id === firstNonTermiantedEmployment?.employment?.id}                                     
                                    />);
                            })
                        }
                    </div> :
                    RenderNotAuthorized() }                    
                </MainContent>
                <Hidden smDown>
                    <SideBarContent>
                        <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                    </SideBarContent>
                </Hidden>
            </PageContainer>

        </>
    );
}